import Axios from "axios";
import config from "../../config";
import { ISubscriptionsRequest } from "@/domain/ISubscriptions";
import store from "@/store";
import {
    IBirthdateRequest,
    IDeleteBonuscardRequest,
    IDeleteCommunicationDeviceRequest,
    ISaveBonuscardRequest,
    ISaveCommunicationDeviceRequest
} from "@/domain/ISaveInfo";
import { IAuthorizedPersonRequest } from "@/domain/ITravelManagerOrAuthorizedPerson";

export abstract class InfoApi {
    private static axios = Axios.create(
        {
            baseURL: config.API_URL,
            headers: {
                common: {
                    'Content-Type': 'application/json'
                }
            }
        }
    )

    static async subscribe(SubscriptionsRequest: ISubscriptionsRequest): Promise<any | null> {
        const url = "/list/subscribe?email_type=" + SubscriptionsRequest.email_type + "&email=" + SubscriptionsRequest.email + "&list=" + SubscriptionsRequest.list

        const encodedToken = store.state.token
        try {
            const response = await this.axios.get<any>(url, { headers: { "Authentication-Estravel": 'Bearer ' + encodedToken } });

            if (response.status === 200) {
                return response.data;
            }
            return null;
        } catch (error) {
            console.error('error: ', (error as Error).message);
            return null;
        }
    }

    static async unsubscribe(SubscriptionsRequest: ISubscriptionsRequest): Promise<any | null> {
        const url = "/list/unsubscribe?email_type=" + SubscriptionsRequest.email_type + "&email=" + SubscriptionsRequest.email + "&list=" + SubscriptionsRequest.list

        const encodedToken = store.state.token
        try {
            const response = await this.axios.get<any>(url, { headers: { "Authentication-Estravel": 'Bearer ' + encodedToken } });

            if (response.status === 200) {
                return response.data;
            }
            return null;
        } catch (error) {
            console.error('error: ', (error as Error).message);
            return null;
        }
    }

    static async saveBirthdate(BirthdateRequest: IBirthdateRequest): Promise<any | null> {
        const url = "/save_birthdate?customer_id=" + BirthdateRequest.customerId + "&birth_date=" + BirthdateRequest.birthDate
        const encodedToken = store.state.token
        try {
            // TODO: use put to update/modify birth date and post to add new? Where and when should this really be used?
            const response = await this.axios.put<any>(url, { headers: { "Authentication-Estravel": 'Bearer ' + encodedToken } });

            if (response.status === 200) {
                return response.data;
            }
            return null;
        } catch (error) {
            console.error('error: ', (error as Error).message);
            return null;
        }
    }

    static async saveCommDevice(SaveCommunicationDeviceRequest: ISaveCommunicationDeviceRequest): Promise<any | null> {
        let url = "/save_commdevice?customer_id=" + SaveCommunicationDeviceRequest.customerId + "&type=" + SaveCommunicationDeviceRequest.type + "&value=" + SaveCommunicationDeviceRequest.value;
        const encodedToken = store.state.token
        try {
            const isUpdate = SaveCommunicationDeviceRequest.communicationDeviceId !== '';
            url = `${url}${isUpdate ? "&communication_device_id=" + SaveCommunicationDeviceRequest.communicationDeviceId : ""}`;
            // const response = await this.axios.post<any>(url, { headers: { "Authentication-Estravel": 'Bearer ' + encodedToken } });
            const method = 'post';
            const response = await this.axios({
                method,
                url,
                headers: { "Authentication-Estravel": 'Bearer ' + encodedToken }
            });

            if (response.status === 200) {
                return response.data;
            } else if (response.data === 201) {
                return response.data;
            }
            return null;
        } catch (error) {
            console.error('error: ', (error as Error).message);
            return null;
        }
    }

    static async deleteCommDevice(DeleteCommunicationDeviceRequest: IDeleteCommunicationDeviceRequest): Promise<any | null> {
        const url = "/delete_commdevice?customer_id=" + DeleteCommunicationDeviceRequest.customerId + "&communication_device_id=" + DeleteCommunicationDeviceRequest.communicationDeviceId;
        const encodedToken = store.state.token
        try {
            const response = await this.axios.delete<any>(url, { headers: { "Authentication-Estravel": 'Bearer ' + encodedToken } });

            if (response.status === 200) {
                return response.data;
            }
            return null;
        } catch (error) {
            console.error('error: ', (error as Error).message);
            return null;
        }
    }

    static async saveBonuscard(SaveBonuscardRequest: ISaveBonuscardRequest): Promise<any | null> {
        let url = "/save_bonuscard?customer_id=" + SaveBonuscardRequest.customerId + "&bonuscard_name=" + SaveBonuscardRequest.bonuscardName + "&bonuscard_number=" + SaveBonuscardRequest.bonuscardNumber + "&bonuscard_valid_until=" + SaveBonuscardRequest.bonuscardValidUntil + "&toplevel=" + SaveBonuscardRequest.toplevel;
        const encodedToken = store.state.token
        try {
            const isUpdate = SaveBonuscardRequest.bonuscardId !== ''
            url = `${url}${isUpdate ? "&bonuscard_id=" + SaveBonuscardRequest.bonuscardId : ""}`;
            const method = 'post';
            const response = await this.axios({
                method,
                url,
                headers: { "Authentication-Estravel": 'Bearer ' + encodedToken }
            });

            if (response.status === 200) { // updated
                return response.data;
            } else if (response.data === 201) { // created
                return response.data;
            }
            return null;
        } catch (error) {
            console.error('error: ', (error as Error).message);
            return null;
        }
    }

    static async deleteBonuscard(DeleteBonuscardRequest: IDeleteBonuscardRequest): Promise<any | null> {
        const url = "/delete_bonuscard?customer_id=" + DeleteBonuscardRequest.customerId + "&bonuscard_id=" + DeleteBonuscardRequest.bonuscardId;
        const encodedToken = store.state.token
        try {
            const response = await this.axios.delete<any>(url, { headers: { "Authentication-Estravel": 'Bearer ' + encodedToken } });

            if (response.status === 200) {
                return response.data;
            }
            return null;
        } catch (error) {
            console.error('error: ', (error as Error).message);
            return null;
        }
    }

    static async addAuthorizedPerson(AuthorizedPersonRequest: IAuthorizedPersonRequest): Promise<any | null> {
        let url = "/add_authorized_person?customer_id=" + AuthorizedPersonRequest.customer_id + "&first_name=" + AuthorizedPersonRequest.first_name + "&last_name=" + AuthorizedPersonRequest.last_name + "&country_of_residence=" + AuthorizedPersonRequest.country_of_residence + "&language=" + AuthorizedPersonRequest.language;
        if (AuthorizedPersonRequest.personal_code !== "") {
            url += "&personal_code=" + AuthorizedPersonRequest.personal_code;
        }
        if (AuthorizedPersonRequest.birth_date !== "") {
            url += "&birth_date=" + AuthorizedPersonRequest.birth_date;
        }
        if (AuthorizedPersonRequest.business_email !== "") {
            url += "&business_email=" + AuthorizedPersonRequest.business_email;
        }
        if (AuthorizedPersonRequest.business_phone !== "") {
            url += "&business_phone=" + AuthorizedPersonRequest.business_phone;
        }
        if (AuthorizedPersonRequest.business_mobile_phone !== "") {
            url += "&business_mobile_phone=" + AuthorizedPersonRequest.business_mobile_phone;
        }
        if (AuthorizedPersonRequest.start_date !== "") {
            url += "&start_date=" + AuthorizedPersonRequest.start_date;
        }
        if (AuthorizedPersonRequest.end_date !== "") {
            url += "&end_date=" + AuthorizedPersonRequest.end_date;
        }
        const encodedToken = store.state.token
        try {
            const method = 'post';
            return await this.axios({
                method,
                url,
                headers: { "Authentication-Estravel": 'Bearer ' + encodedToken }
            });
        } catch (error) {
            console.error('error: ', (error as Error).message);
            if (error.response.status === 400) return error.response;
            return null;
        }
    }

    static async updateAuthorizedPerson(AuthorizedPersonRequest: IAuthorizedPersonRequest): Promise<any | null> {
        const url = "/update_authorized_person?customer_id=" + AuthorizedPersonRequest.customer_id +
            "&authperson_id=" + AuthorizedPersonRequest.authperson_id +
            "&country_of_residence=" + AuthorizedPersonRequest.country_of_residence +
            "&language=" + AuthorizedPersonRequest.language +
            "&business_email=" + AuthorizedPersonRequest.business_email +
            "&business_phone=" + AuthorizedPersonRequest.business_phone +
            "&business_mobile_phone=" + AuthorizedPersonRequest.business_mobile_phone +
            "&end_date=" + AuthorizedPersonRequest.end_date;
        const encodedToken = store.state.token
        try {
            const method = 'put';
            return await this.axios({
                method,
                url,
                headers: { "Authentication-Estravel": 'Bearer ' + encodedToken }
            });
        } catch (error) {
            console.error('error: ', (error as Error).message);
            return null;
        }
    }
}
